'use client';

import {useMemo} from 'react';
import {ButtonSwitch, ButtonSwitchTrigger} from '@udemy-v2/react-structure-components';
import {Tooltip} from '@udemy-v2/react-popup-components';
import {Icon} from '@udemy-v2/icons';
import {useI18n, useFormatNumber} from '@udemy-v2/i18n';
import classNames from 'classnames';

import {useMatchMedia} from '@udemy-v2/design-system-utils';
import {CourseRating} from '@udemy-v2/graphql';

import styles from './reviews-tabs.module.scss';

export enum ReviewTabType {
    ALL_REVIEWS = 'all-reviews',
    THIS_VARIATION = 'this-variation',
}

interface ReviewsTitleWithInfoProps {
    showInfoTooltip?: boolean;
    infoTooltipText?: string;
    className?: string;
    rating?: CourseRating | null;
}

export function ReviewsTitleWithInfo({
    showInfoTooltip,
    infoTooltipText,
    rating,
    className,
}: ReviewsTitleWithInfoProps) {
    const isXsMax = useMatchMedia('xs-max');
    const {interpolate, ninterpolate, gettext} = useI18n();
    const {formatNumber} = useFormatNumber();

    if (!rating) {
        return null;
    }

    const courseRatingNumber = formatNumber(rating.average, {
        maximumFractionDigits: 1,
    });
    const courseRating = isXsMax
        ? courseRatingNumber
        : interpolate(gettext('%(rating)s course rating'), {rating: courseRatingNumber}, true);
    const ratings =
        rating.count == 0
            ? null
            : ninterpolate('%(rating)s rating', '%(rating)s ratings', rating.count, {
                  rating: formatNumber(rating.count, {
                      notation: 'compact',
                  }),
              });

    return (
        <div className={classNames(styles['reviews-title'], className)}>
            <Icon glyph="rating-star" color="warning" size="medium" label={false} />
            <span className={'ud-heading-xl'}>
                {rating.count > 0 ? courseRating : gettext('No ratings yet')}
            </span>
            {rating.count > 0 && (
                <>
                    <Icon glyph="bullet" color="subdued" size="xsmall" label={false} />
                    <span className={'ud-heading-xl'}>{ratings}</span>
                    {showInfoTooltip && infoTooltipText && (
                        <Tooltip
                            placement="bottom"
                            renderTrigger={(triggerProps) => (
                                <span {...triggerProps} className={styles['info-tooltip-trigger']}>
                                    <Icon glyph="info-outline" label={false} size="medium" />
                                </span>
                            )}
                        >
                            {infoTooltipText}
                        </Tooltip>
                    )}
                </>
            )}
        </div>
    );
}

interface ReviewsTabsProps {
    allReviewsCount: number;
    variationReviewsCount: number;
    activeTab: ReviewTabType;
    setActiveTab: (tab: ReviewTabType) => void;
    className?: string;
    renderAllReviews: () => React.ReactNode;
    renderVariationReviews: () => React.ReactNode;
}

export function ReviewsTabs({
    allReviewsCount,
    variationReviewsCount,
    activeTab,
    setActiveTab,
    className,
    renderAllReviews,
    renderVariationReviews,
}: ReviewsTabsProps) {
    const {gettext, ninterpolate} = useI18n();
    const {formatNumber} = useFormatNumber();

    const handleValueChange = (value: string) => {
        setActiveTab(value as ReviewTabType);
    };

    const allReviewsLabel = useMemo(() => {
        const count = formatNumber(allReviewsCount, {notation: 'compact'});
        return ninterpolate('All reviews (%(count)s)', 'All reviews (%(count)s)', allReviewsCount, {
            count,
        });
    }, [allReviewsCount, formatNumber, ninterpolate]);

    const variationReviewsLabel = useMemo(() => {
        const count = formatNumber(variationReviewsCount, {notation: 'compact'});
        return ninterpolate(
            'This variation (%(count)s)',
            'This variation (%(count)s)',
            variationReviewsCount,
            {count},
        );
    }, [variationReviewsCount, formatNumber, ninterpolate]);

    return (
        <div className={className} data-purpose="reviews-tabs">
            <ButtonSwitch
                aria-label={gettext('Reviews filter')}
                activeValue={activeTab}
                onValueChange={handleValueChange}
                size="medium"
            >
                <ButtonSwitchTrigger
                    aria-controls="all-reviews-panel"
                    id="all-reviews-trigger"
                    value={ReviewTabType.ALL_REVIEWS}
                    label={allReviewsLabel}
                />
                <ButtonSwitchTrigger
                    aria-controls="this-variation-panel"
                    id="this-variation-trigger"
                    value={ReviewTabType.THIS_VARIATION}
                    label={variationReviewsLabel}
                />
            </ButtonSwitch>

            <div
                id="all-reviews-panel"
                role="tabpanel"
                aria-labelledby="all-reviews-trigger"
                tabIndex={0}
                className={classNames(styles['tab-content'], {
                    [styles['hidden']]: activeTab !== ReviewTabType.ALL_REVIEWS,
                })}
            >
                {renderAllReviews()}
            </div>
            <div
                id="this-variation-panel"
                role="tabpanel"
                aria-labelledby="this-variation-trigger"
                tabIndex={0}
                className={classNames(styles['tab-content'], {
                    [styles['hidden']]: activeTab !== ReviewTabType.THIS_VARIATION,
                })}
            >
                {renderVariationReviews()}
            </div>
        </div>
    );
}
